/**
 * Nav Color Sync
 * Dynamically matches the header's background and text color
 * to the color suite of whichever section it currently overlaps.
 *
 * Overlay-aware: when the header is in overlay mode (transparent,
 * over the hero), background is only shown on hover. When stuck
 * (scrolled), background applies normally.
 */
(function () {
  function init() {
    var header = document.getElementById('SiteHeader');
    if (!header) return;

    // Force header to always be stuck (fixed at top) immediately.
    // Impulse normally waits for 250px scroll — we skip that.
    header.classList.add('site-header--stuck');
    header.classList.add('site-header--opening');



    // Prevent Impulse's scrollHandler from removing the stuck class
    // by re-adding it whenever it gets removed
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.attributeName === 'class' && !header.classList.contains('site-header--stuck')) {
          header.classList.add('site-header--stuck');
          header.classList.add('site-header--opening');
        }
      });
    }).observe(header, { attributes: true, attributeFilter: ['class'] });

    // Move icons into a small utility bar above the header (desktop only).
    // On mobile, icons stay in the header so they're always visible.
    // The bar is built once; placeIcons() moves the icons in or out and
    // attaches/detaches the bar, and re-runs on resize / orientation change
    // (768px matches Impulse's own header breakpoint). Previously this was
    // evaluated once at load, so rotating a tablet to portrait left the icons
    // inside a bar that is hidden on mobile.
    var utilBar = null;
    var utilWrap = null;
    var utilInner = null;
    var mobileQuery = window.matchMedia('(max-width: 768px)');
    var icons = header.querySelector('.header-item--icons');
    var iconsHome = icons ? icons.parentNode : null;
    var iconsNext = icons ? icons.nextSibling : null;

    if (icons) {
      utilWrap = document.createElement('div');
      utilWrap.className = 'custom-utility-bar-wrap';

      utilBar = document.createElement('div');
      utilBar.id = 'CustomUtilityBar';
      utilBar.className = 'custom-utility-bar small--hide';
      utilInner = document.createElement('div');
      utilInner.className = 'custom-utility-bar__inner';
      utilBar.appendChild(utilInner);
      utilWrap.appendChild(utilBar);
    }

    // True while the utility bar is in the document (desktop layout).
    function utilActive() {
      return !!(utilWrap && utilWrap.parentNode);
    }

    function placeIcons() {
      if (!icons) return;
      if (mobileQuery.matches) {
        if (icons.parentNode !== iconsHome) {
          var ref = (iconsNext && iconsNext.parentNode === iconsHome) ? iconsNext : null;
          iconsHome.insertBefore(icons, ref);
        }
        if (utilWrap.parentNode) utilWrap.parentNode.removeChild(utilWrap);
      } else {
        if (icons.parentNode !== utilInner) utilInner.appendChild(icons);
        if (!utilWrap.parentNode) {
          var stickyWrapper = header.closest('.header-sticky-wrapper');
          var insertTarget = stickyWrapper || header;
          insertTarget.parentNode.insertBefore(utilWrap, insertTarget);
        }
      }
    }
    placeIcons();

    // Replace Impulse's <img> logos with custom inline SVGs
    // that respect currentColor and --colorNavAccent
    var customLogoContainer = document.getElementById('CustomHeaderLogo');
    var logoSlot = header.querySelector('.site-header__logo');
    if (customLogoContainer && logoSlot) {
      var wrapper = customLogoContainer.querySelector('.custom-logo-wrapper');
      if (wrapper) {
        logoSlot.innerHTML = '';
        logoSlot.appendChild(wrapper);
        wrapper.style.display = '';
        customLogoContainer.remove();
      }
    }

    var root = document.documentElement;
    var defaultBg = getComputedStyle(root).getPropertyValue('--colorNav').trim();
    var defaultText = getComputedStyle(root).getPropertyValue('--colorNavText').trim();
    var defaultAccent = getComputedStyle(root).getPropertyValue('--accent-ochre').trim();
    var isOverlay = header.dataset.overlay === 'true';

    // Direct DOM fill updates for badge SVG paths (bypasses CSS variable issues)
    function updateBadgeFills(accent, titleDark) {
      var accentPaths = header.querySelectorAll('[data-fill="accent"]');
      var monogramPaths = header.querySelectorAll('[data-fill="monogram"]');
      accentPaths.forEach(function (p) { p.setAttribute('fill', accent); });
      monogramPaths.forEach(function (p) { p.setAttribute('fill', titleDark); });
    }

    function applyColors(bg, text, accent, lightTitle, lightTitleDark, isDark) {
      var resolvedAccent = accent || defaultAccent;
      var resolvedTitleDark = lightTitleDark || text;
      // Toggle dark-section flag so CSS can key off it (e.g. dropdown link colors)
      if (isDark) {
        header.classList.add('site-header--dark-section');
      } else {
        header.classList.remove('site-header--dark-section');
      }
      // Set accent for badge SVG and wordmark
      header.style.setProperty('--colorNavAccent', resolvedAccent);
      // Light-mode title colors for logo (unaffected by dark mode)
      header.style.setProperty('--colorNavLightTitle', lightTitle || text);
      header.style.setProperty('--colorNavLightTitleDark', resolvedTitleDark);
      // Update badge SVG fills directly
      updateBadgeFills(resolvedAccent, resolvedTitleDark);
      var isStuck = header.classList.contains('site-header--stuck');
      var isCollapsed = header.classList.contains('site-header--collapsed');
      var isRevealed = header.classList.contains('site-header--reveal');

      // Always set variables so children (nav links, mega menus) inherit
      header.style.setProperty('--colorNav', bg);
      header.style.setProperty('--colorNavText', text);

      // Always apply text color so nav/logo inherits section colors
      header.style.setProperty('color', text);

      var atTop = header.classList.contains('site-header--at-top');

      if (atTop) {
        // At top of page — transparent overlay
        header.style.removeProperty('background-color');
        header.classList.remove('site-header--bg-visible');
      } else if (isStuck && isCollapsed && !isRevealed) {
        // Collapsed sticky — transparent, hover shows bg
        header.style.removeProperty('background-color');
        header.classList.remove('site-header--bg-visible');
      } else {
        // Revealed or hovered — solid background
        header.style.setProperty('background-color', bg, 'important');
        header.classList.add('site-header--bg-visible');
      }
    }

    // Hover behavior: show bg on hover for overlay and collapsed states
    function showHeaderBg() {
      var bg = header.style.getPropertyValue('--colorNav');
      if (bg) {
        header.style.setProperty('background-color', bg, 'important');
        header.classList.add('site-header--bg-visible');
      }
    }
    function hideHeaderBg() {
      header.style.removeProperty('background-color');
      header.classList.remove('site-header--bg-visible');
    }

    function handleMouseEnter() {
      header.classList.add('site-header--hovered');
      // Show utility bar fixed at top when header is hovered away from top
      if (utilActive() && !header.classList.contains('site-header--at-top')) {
        utilBar.classList.add('custom-utility-bar--fixed');
        header.style.setProperty('top', utilBarHeight + 'px');
      }
      var isStuck = header.classList.contains('site-header--stuck');
      var isCollapsed = header.classList.contains('site-header--collapsed');
      var atTop = header.classList.contains('site-header--at-top');

      if (atTop || (!isStuck && isOverlay) || (isStuck && isCollapsed)) {
        showHeaderBg();
      }
    }
    function handleMouseLeave(e) {
      // Don't hide if moving between header and utility bar
      var related = e.relatedTarget;
      if (utilBar && (utilBar.contains(related) || related === utilBar)) return;
      if (header.contains(related) || related === header) return;

      header.classList.remove('site-header--hovered');
      if (utilActive()) {
        utilBar.classList.remove('custom-utility-bar--fixed');
        // Restore scroll-based offset (don't hardcode 0 — at top of page it should be utilBarHeight)
        var currentScroll = window.pageYOffset || 0;
        var offset = Math.max(0, utilBarHeight - currentScroll);
        header.style.setProperty('top', offset + 'px');
      }

      var isStuck = header.classList.contains('site-header--stuck');
      var isCollapsed = header.classList.contains('site-header--collapsed');
      var isRevealed = header.classList.contains('site-header--reveal');
      var atTop = header.classList.contains('site-header--at-top');

      if (atTop || (!isStuck && isOverlay) || (isStuck && isCollapsed && !isRevealed)) {
        hideHeaderBg();
      }
    }

    // Hover reveal only on devices that can hover. On touch, a tap fires a
    // synthetic mouseenter with no matching mouseleave, which left the header
    // stuck in its hovered state (bg visible, wordmark logo swapped in).
    if (window.matchMedia('(hover: hover)').matches) {
      header.addEventListener('mouseenter', handleMouseEnter);
      header.addEventListener('mouseleave', handleMouseLeave);
      if (utilBar) {
        utilBar.addEventListener('mouseenter', handleMouseEnter);
        utilBar.addEventListener('mouseleave', handleMouseLeave);
      }
    }

    function readSectionColors(el) {
      var styles = getComputedStyle(el);
      return {
        bg: styles.getPropertyValue('--token-bg-primary').trim(),
        text: styles.getPropertyValue('--token-text-title').trim(),
        // --token-accent only exists where a custom section sets it inline;
        // bridged stock scopes fall back to the seasonal accent mirror.
        accent: styles.getPropertyValue('--token-accent').trim() ||
          styles.getPropertyValue('--accent-inherit').trim(),
        lightTitle: styles.getPropertyValue('--token-light-title').trim(),
        lightTitleDark: styles.getPropertyValue('--token-light-title-dark').trim()
      };
    }

    // Bridged stock pages (search, cart, blog, list-collections, …) have no
    // custom-color <section> roots — the suite class sits on .main-content
    // via the Impulse → token bridge. Used as the sync fallback below.
    // Note: cannot be added to the section scan itself — custom sections
    // render INSIDE main-content, so it would always match first.
    var bridgedMain = document.querySelector('main.main-content[class*="custom-color-"]');

    function isDarkScope(el) {
      return el.classList.contains('dark-mode') ||
        (el.classList.contains('auto-dark') &&
          window.matchMedia('(prefers-color-scheme: dark)').matches);
    }

    function syncColor() {
      var scrollY = window.pageYOffset || root.scrollTop;
      var headerHeight = header.offsetHeight;
      var headerTop = scrollY;
      var headerBottom = scrollY + headerHeight;

      var sections = document.querySelectorAll('section[class*="custom-color-"]');
      var current = null;
      var next = null;
      var boundaryY = -1;

      for (var i = 0; i < sections.length; i++) {
        var rect = sections[i].getBoundingClientRect();
        var sTop = rect.top + scrollY;
        var sBottom = sTop + rect.height;

        // Find the section the header's midpoint is in (current)
        if (sTop <= headerBottom && sBottom > headerBottom) {
          current = sections[i];
          // Check if this section's bottom boundary is within the header
          if (sBottom > headerTop && sBottom < headerBottom) {
            boundaryY = sBottom;
            // The next section starts at this boundary
            if (i + 1 < sections.length) {
              next = sections[i + 1];
            }
          }
          // Also check if the NEXT section's top is within the header
          if (!next && i + 1 < sections.length) {
            var nextRect = sections[i + 1].getBoundingClientRect();
            var nextTop = nextRect.top + scrollY;
            if (nextTop > headerTop && nextTop < headerBottom) {
              boundaryY = nextTop;
              next = sections[i + 1];
            }
          }
          break;
        }
      }

      // Apply current section colors; fall back to the bridged main-content
      // scope (stock pages), then to the :root defaults.
      var fallback = bridgedMain;
      if (current) {
        var c = readSectionColors(current);
        if (c.bg && c.text) {
          applyColors(c.bg, c.text, c.accent, c.lightTitle || c.text, c.lightTitleDark || c.text, isDarkScope(current));
          fallback = null;
        }
      }
      if (fallback) {
        var b = readSectionColors(fallback);
        if (b.bg && b.text) {
          applyColors(b.bg, b.text, b.accent, b.lightTitle || b.text, b.lightTitleDark || b.text, isDarkScope(fallback));
        } else {
          applyColors(defaultBg, defaultText, defaultAccent, defaultText, defaultText, false);
        }
      } else if (!current) {
        applyColors(defaultBg, defaultText, defaultAccent, defaultText, defaultText, false);
      }

      // Sync utility bar colors to current section (matching header)
      if (utilActive()) {
        var ubSource = current || (sections.length ? sections[0] : null) || bridgedMain;
        if (ubSource) {
          var ub = readSectionColors(ubSource);
          if (ub.text) {
            utilBar.style.setProperty('color', ub.text);
            utilBar.style.setProperty('--colorNavText', ub.text);
          }
          if (ub.accent) utilBar.style.setProperty('--colorNavAccent', ub.accent);
        }
        // Keep --custom-ub-bg from first section (used for at-top hover reveal)
        var ubBgSource = sections.length ? sections[0] : bridgedMain;
        if (ubBgSource) {
          var ubBg = readSectionColors(ubBgSource);
          if (ubBg.bg) utilBar.style.setProperty('--custom-ub-bg', ubBg.bg);
        }
      }

      // Apply split-color effect on logo via CSS gradient stop
      if (next && boundaryY > 0) {
        var splitPercent = ((boundaryY - headerTop) / headerHeight) * 100;
        splitPercent = Math.max(0, Math.min(100, splitPercent));

        var n = readSectionColors(next);
        header.style.setProperty('--colorNavAccentNext', n.accent || defaultAccent);
        header.style.setProperty('--colorNavLightTitleDarkNext', n.lightTitleDark || n.text || defaultText);
        header.style.setProperty('--logo-split', splitPercent + '%');
      } else {
        // No boundary — single color (split at 100%)
        header.style.setProperty('--logo-split', '100%');
      }
    }

    // Scroll-direction detection: collapse to logo-only when scrolling
    // down, reveal full nav when scrolling up
    var lastScrollY = window.pageYOffset || 0;

    var utilBarHeight = utilActive() ? utilBar.offsetHeight - 16 : 0;

    function updateScrollDirection() {
      var currentScrollY = window.pageYOffset || root.scrollTop;
      var atTop = currentScrollY < 10;

      // Slide the fixed header down while the utility bar is in view
      if (utilActive()) {
        if (header.classList.contains('site-header--hovered') && !atTop) {
          header.style.setProperty('top', utilBarHeight + 'px');
        } else {
          var offset = Math.max(0, utilBarHeight - currentScrollY);
          header.style.setProperty('top', offset + 'px');
        }
      }

      if (atTop) {
        // At top of page — overlay-like: transparent, full nav visible
        header.classList.remove('site-header--collapsed');
        header.classList.remove('site-header--reveal');
        header.classList.add('site-header--at-top');
        header.style.removeProperty('background-color');
      } else {
        header.classList.remove('site-header--at-top');

        // Always collapsed when scrolled
        if (!header.classList.contains('site-header--collapsed')) {
          header.classList.add('site-header--collapsed');
        }

        if (currentScrollY < lastScrollY) {
          // Scrolling up — reveal full nav
          header.classList.add('site-header--reveal');
        } else {
          // Scrolling down — collapse to logo only
          header.classList.remove('site-header--reveal');
        }
      }

      lastScrollY = currentScrollY;
    }

    // Initial state
    if ((window.pageYOffset || 0) < 10) {
      header.classList.add('site-header--at-top');
    } else {
      header.classList.add('site-header--collapsed');
    }

    // Set initial header offset below utility bar
    if (utilActive()) {
      var initScroll = window.pageYOffset || 0;
      header.style.setProperty('top', Math.max(0, utilBarHeight - initScroll) + 'px');
    }

    // Initial sync, plus a deferred retry in case computed styles from
    // section <style> blocks haven't resolved yet (seen in theme editor preview).
    syncColor();
    requestAnimationFrame(syncColor);
    setTimeout(syncColor, 100);

    var ticking = false;
    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(function () {
          syncColor();
          updateScrollDirection();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    // Re-sync when system color-scheme flips so auto-dark sections update
    var darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
    if (darkModeQuery.addEventListener) {
      darkModeQuery.addEventListener('change', syncColor);
    } else if (darkModeQuery.addListener) {
      darkModeQuery.addListener(syncColor);
    }

    // Re-place the icons and re-sync colours after a resize or rotation.
    // Debounced: iOS fires resize on every URL-bar show/hide while scrolling.
    var resizeTimer = null;
    function onViewportChange() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        placeIcons();
        utilBarHeight = utilActive() ? utilBar.offsetHeight - 16 : 0;
        syncColor();
      }, 150);
    }
    window.addEventListener('resize', onViewportChange, { passive: true });
    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', onViewportChange);
    } else if (mobileQuery.addListener) {
      mobileQuery.addListener(onViewportChange);
    }

    // Theme editor: re-sync whenever Shopify re-renders, adds, removes, or
    // reorders a section. Without this, the header stays stuck on its initial
    // colors when the merchant edits a section in the admin preview.
    if (window.Shopify && window.Shopify.designMode) {
      var editorResync = function () { requestAnimationFrame(syncColor); };
      document.addEventListener('shopify:section:load', editorResync);
      document.addEventListener('shopify:section:unload', editorResync);
      document.addEventListener('shopify:section:reorder', editorResync);
      document.addEventListener('shopify:section:select', editorResync);
      document.addEventListener('shopify:section:deselect', editorResync);
    }

  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
